import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import api from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { toast } from 'sonner';
import { Loader2, ChevronRight, RefreshCw, TrendingUp, TrendingDown, Minus } from 'lucide-react';

const HEALTH = {
  'needs attention': 'bg-rose-100 text-rose-700',
  'some issues': 'bg-amber-100 text-amber-800',
  clean: 'bg-emerald-100 text-emerald-700',
};
const SEV_CELL = { critical: 'text-rose-600', high: 'text-orange-500', medium: 'text-amber-500', low: 'text-slate-400' };

export default function CourseQaManagementPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [severity, setSeverity] = useState('');
  const [status, setStatus] = useState('');
  const [reviewer, setReviewer] = useState('');
  const [days, setDays] = useState(0);

  const load = () => {
    setLoading(true);
    api.get('/course-qa/management', { params: { severity, status, reviewer, days } })
      .then(r => setData(r.data))
      .catch(e => toast.error(e.response?.data?.detail || 'Could not load the management report'))
      .finally(() => setLoading(false));
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(load, [severity, status, reviewer, days]);

  const rows = (data?.courses || []).filter(c =>
    !q || (c.title || '').toLowerCase().includes(q.toLowerCase()) || (c.slug || '').includes(q.toLowerCase()));
  const navTab = (to, label) => (
    <button key={to} onClick={() => navigate(to)}
      className={`text-xs font-medium px-2.5 py-1 rounded-sm transition-colors ${location.pathname === to ? 'text-white' : 'text-slate-300 hover:text-white'}`}
      data-testid={`qa-nav-${label.toLowerCase().replace(/ /g, '-')}`}>{label}</button>
  );

  const Delta = ({ d }) => {
    if (!d) return <span className="text-[10px] text-slate-300">first run</span>;
    const net = (d.critical || 0) + (d.high || 0) + (d.medium || 0) + (d.low || 0);
    if (net === 0) return <span className="text-[10px] text-slate-400 inline-flex items-center gap-0.5"><Minus className="w-3 h-3" /> no change</span>;
    return net < 0
      ? <span className="text-[10px] text-emerald-600 inline-flex items-center gap-0.5"><TrendingDown className="w-3 h-3" /> {Math.abs(net)} fewer</span>
      : <span className="text-[10px] text-rose-600 inline-flex items-center gap-0.5"><TrendingUp className="w-3 h-3" /> {net} more</span>;
  };

  return (
    <div className="-m-4 sm:-m-6" data-testid="course-qa-management-page">
      <div className="bg-[#0f2b4c] px-4 sm:px-6 py-3.5 flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-baseline gap-3">
          <span className="text-white text-base sm:text-lg font-bold tracking-tight">Course Page QA</span>
          <span className="text-slate-300 text-xs hidden sm:inline">Management reporting</span>
        </div>
        <div className="flex items-center gap-1 flex-wrap">
          {navTab('/course-qa', 'Audit a page')}
          {navTab('/course-qa/all', 'Audit all courses')}
          {navTab('/course-qa/defects', 'Defect board')}
          {navTab('/course-qa/rules', 'Rules guide')}
          {navTab('/course-qa/management', 'Management')}
        </div>
      </div>

      <div className="p-4 sm:p-6 bg-slate-50 min-h-[calc(100vh-7rem)] space-y-5">
        <div className="grid grid-cols-2 lg:grid-cols-6 gap-3">
          {[['Courses audited', data?.audited_courses ?? '—', 'text-slate-800'],
            ['Open issues', data?.open_issues ?? '—', 'text-slate-800'],
            ['Critical', data?.totals?.critical ?? '—', 'text-rose-600'],
            ['High', data?.totals?.high ?? '—', 'text-orange-500'],
            ['Medium', data?.totals?.medium ?? '—', 'text-amber-500'],
            ['Low', data?.totals?.low ?? '—', 'text-slate-400']].map(([label, value, cls]) => (
            <div key={label} className="bg-white border border-slate-200 rounded-md px-4 py-3" data-testid={`qa-mgmt-${label.toLowerCase().replace(/ /g, '-')}`}>
              <div className={`text-2xl font-bold ${cls}`}>{value}</div>
              <div className="text-[10px] uppercase tracking-wider font-semibold text-slate-400 mt-0.5">{label}</div>
            </div>
          ))}
        </div>

        <div className="bg-white border border-slate-200 rounded-md">
          <div className="p-4 border-b border-slate-100 flex items-center gap-2 flex-wrap">
            <h2 className="text-sm font-bold text-slate-800 mr-auto">
              Course-wise QA status
              <span className="block text-[10px] font-normal text-slate-400">Counts are issues still open</span>
            </h2>
            <Input value={q} onChange={e => setQ(e.target.value)} placeholder="Search courses…"
              className="h-8 w-48 rounded-md text-xs" data-testid="qa-mgmt-search" />
            <select value={status} onChange={e => setStatus(e.target.value)}
              className="h-8 text-xs border border-slate-200 rounded-md px-2 bg-white" data-testid="qa-mgmt-status">
              <option value="">All statuses</option>
              <option value="needs attention">Needs attention</option>
              <option value="some issues">Some issues</option>
              <option value="clean">Clean</option>
            </select>
            <select value={severity} onChange={e => setSeverity(e.target.value)}
              className="h-8 text-xs border border-slate-200 rounded-md px-2 bg-white" data-testid="qa-mgmt-severity">
              <option value="">Any severity</option>
              {['critical', 'high', 'medium', 'low'].map(s => <option key={s} value={s}>Has {s}</option>)}
            </select>
            <select value={reviewer} onChange={e => setReviewer(e.target.value)}
              className="h-8 text-xs border border-slate-200 rounded-md px-2 bg-white" data-testid="qa-mgmt-reviewer">
              <option value="">Any reviewer</option>
              {(data?.reviewers || []).map(p => <option key={p.name} value={p.name}>{p.name}</option>)}
            </select>
            <select value={days} onChange={e => setDays(Number(e.target.value))}
              className="h-8 text-xs border border-slate-200 rounded-md px-2 bg-white" data-testid="qa-mgmt-days">
              <option value={0}>All time</option>
              <option value={7}>Last 7 days</option>
              <option value={30}>Last 30 days</option>
            </select>
            <Button variant="outline" size="sm" onClick={load} className="rounded-md h-8" data-testid="qa-mgmt-refresh">
              <RefreshCw className="w-3.5 h-3.5" />
            </Button>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-left">
              <thead>
                <tr className="text-[10px] uppercase tracking-wider text-slate-400 border-b border-slate-100">
                  <th className="px-4 py-2 font-semibold">Course</th>
                  <th className="px-2 py-2 font-semibold">Slug</th>
                  <th className="px-2 py-2 font-semibold text-center">Crit</th>
                  <th className="px-2 py-2 font-semibold text-center">High</th>
                  <th className="px-2 py-2 font-semibold text-center">Med</th>
                  <th className="px-2 py-2 font-semibold text-center">Low</th>
                  <th className="px-2 py-2 font-semibold text-center">Open</th>
                  <th className="px-2 py-2 font-semibold">Checklist</th>
                  <th className="px-2 py-2 font-semibold">Vs last run</th>
                  <th className="px-2 py-2 font-semibold">Last audit</th>
                  <th className="px-2 py-2 font-semibold">Status</th>
                  <th className="px-2 py-2" />
                </tr>
              </thead>
              <tbody>
                {loading && <tr><td colSpan={12} className="p-6 text-center text-xs text-slate-400"><Loader2 className="w-4 h-4 animate-spin inline mr-2" />Loading…</td></tr>}
                {!loading && rows.length === 0 && <tr><td colSpan={12} className="p-6 text-center text-xs text-slate-400">No audited courses match these filters.</td></tr>}
                {rows.map(c => (
                  <tr key={c.slug} className="border-b border-slate-50 hover:bg-slate-50/70" data-testid={`qa-mgmt-row-${c.slug}`}>
                    <td className="px-4 py-2.5 text-xs font-semibold text-slate-800">{c.title}
                      {c.page_status === 'draft' && <span className="ml-1.5 text-[9px] uppercase font-bold text-amber-700 bg-amber-100 px-1 py-0.5 rounded">draft</span>}
                    </td>
                    <td className="px-2 py-2.5 text-[10px] font-mono text-slate-400 max-w-[160px] truncate">{c.slug}</td>
                    {['critical', 'high', 'medium', 'low'].map(s => (
                      <td key={s} className={`px-2 py-2.5 text-xs font-bold text-center ${c.counts[s] ? SEV_CELL[s] : 'text-slate-300'}`}>{c.counts[s]}</td>
                    ))}
                    <td className="px-2 py-2.5 text-xs text-center text-slate-700">{c.open_issues}</td>
                    <td className="px-2 py-2.5">
                      <div className="flex items-center gap-1.5">
                        <div className="h-1.5 w-16 bg-slate-100 rounded-full overflow-hidden">
                          <div className="h-full bg-blue-500" style={{ width: `${(c.checklist_done / c.checklist_total) * 100}%` }} />
                        </div>
                        <span className="text-[10px] text-slate-400">{c.checklist_done}/{c.checklist_total}</span>
                      </div>
                    </td>
                    <td className="px-2 py-2.5"><Delta d={c.delta} /></td>
                    <td className="px-2 py-2.5 text-[10px] text-slate-500">
                      {String(c.audited_at).slice(0, 10)}<span className="block text-slate-400">{c.audited_by}</span>
                    </td>
                    <td className="px-2 py-2.5">
                      <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full capitalize ${HEALTH[c.health]}`}>{c.health}</span>
                    </td>
                    <td className="px-2 py-2.5 text-right">
                      <button onClick={() => navigate(`/course-qa?audit=${c.audit_id}`)}
                        className="text-blue-600 hover:text-blue-800" data-testid={`qa-mgmt-open-${c.slug}`}>
                        <ChevronRight className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {(data?.retired || []).length > 0 && (
          <p className="text-[11px] text-slate-400" data-testid="qa-mgmt-retired">
            {data.retired.length} earlier audit(s) belong to courses that are no longer in the catalogue
            ({data.retired.map(r => r.slug).slice(0, 3).join(', ')}{data.retired.length > 3 ? '…' : ''}) and are not counted above.
          </p>
        )}

        <div className="grid lg:grid-cols-2 gap-5">
          <div className="bg-white border border-slate-200 rounded-md">
            <h2 className="text-sm font-bold text-slate-800 p-4 pb-2">Reviewer activity</h2>
            {(data?.reviewers || []).length === 0 && <p className="px-4 pb-4 text-xs text-slate-400">No reviewer activity yet.</p>}
            {(data?.reviewers || []).map(p => (
              <div key={p.name} className="px-4 py-2 border-t border-slate-50 flex items-center justify-between" data-testid={`qa-mgmt-reviewer-${p.name}`}>
                <div>
                  <div className="text-xs font-semibold text-slate-800">{p.name}</div>
                  <div className="text-[10px] text-slate-400">{p.role === 'course_qa' ? 'Course QA Reviewer' : String(p.role || '').replace('_', ' ')}</div>
                </div>
                <div className="text-[10px] text-slate-500 text-right">
                  {p.audits} audits · {p.ticks} checklist changes · {p.decisions} issue decisions
                </div>
              </div>
            ))}
          </div>
          <div className="bg-white border border-slate-200 rounded-md">
            <h2 className="text-sm font-bold text-slate-800 p-4 pb-2">Recent activity</h2>
            <div className="max-h-80 overflow-y-auto">
              {(data?.recent || []).map(l => (
                <div key={l.id} className="px-4 py-1.5 border-t border-slate-50 text-[11px] flex gap-2">
                  <span className="text-slate-400 w-28 shrink-0">{String(l.at).slice(5, 16).replace('T', ' ')}</span>
                  <span className="font-semibold text-slate-700 w-28 shrink-0 truncate">{l.user_name}</span>
                  <span className="text-slate-500 flex-1">{l.detail}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
